import React, { useContext, useEffect, useRef, useState } from "react";
import {
    getAssessmentQuestions,
    getPrevAssessmentResponses,
    submitAssessment,
} from "../../services/svt/svt-user";

import ArrowBox from "../common/ArrowBox";
import AssessmentTable from "../common/AssessmentTable";
import Loader from "../common/Loader/Loader";
import { SVTUserContext } from "../../utils/SVTUserContext";
import SubmitConfirmation from "../../resources/submitConfirmation";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const SelfVerificationAssessment = ({ levelsData }) => {
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const [isCancel, setIsCancel] = useState(false);
    const assessmentTableRef = useRef(null);
    const { i18n, t } = useTranslation();
    const { language } = i18n;
    const languageMap = {
        en: 1,
        ch: 19,
        jp: 2
    };
    const currentLanguageId = languageMap[language];
    const [languageId, setLanguageId] = useState(currentLanguageId);

    const {
        selectedEntity,
        setActiveTab,
        activeLevelId,
        assessmentId,
        assessmentResult,
        setAssessmentResult,
        templateName,
        isReview,
        setIsReview,
        isSave,
        setIsSave,
        assessmentQuestions,
        setAssessmentQuestions,
        assessmentResponses,
        setAssessmentResponses,
        setScores,
    } = useContext(SVTUserContext);

    const fetchQuestionsData = async () => {
        try {
            setLoading(true);
            const res = await getAssessmentQuestions(
                assessmentId,
                activeLevelId,
                currentLanguageId
            );
            setAssessmentQuestions(res?.data || []);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPrevResponses = async () => {
        try {
            setLoading(true);
            const res = await getPrevAssessmentResponses(
                assessmentId,
                1,
                selectedEntity,
                languageId
            );
            setAssessmentResponses(res?.data || []);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (assessmentId && activeLevelId) {
            fetchQuestionsData();
            fetchPrevResponses();
        }
    }, [activeLevelId, assessmentId, languageId]);

     useEffect(() => {
        if (language === "ch" || language === "zh") {
            setLanguageId(19);
        } else if (language === "jp") {
            setLanguageId(2);
        }else{
            setLanguageId(1);
        }
    }, [language]);

    const handleSubmit = (action) => {
        setIsModalOpen(true);
        if (action === "submit") {
            setIsSubmit(true);
            setIsSave(false);
        } else if (action === "save") {
            setIsSubmit(false);
        } else if (action === "cancel") {
            setIsCancel(true);
            setIsSave(false);
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setIsCancel(false);
        setIsSave(false);
    };

    const handleModalConfirm = async () => {
        if (isCancel) {
            setIsModalOpen(false);
            setActiveTab("Tab2");
            setIsCancel(false);
            return;
        }
        const targetIds = assessmentQuestions
            .filter((a) => a.isEvidenceRequired)
            .map((b) => {
                return b.id;
            });
        const data = assessmentTableRef.current.getTableData();

        let isEvidenceRequired = false;

        data.forEach((item, index) => {
            if (targetIds.includes(item.questionId) && !item.evidenceFile) {
                toast.error(
                    t("evidenceRequiredError", { index: index + 1 }),
                    {
                        position: "top-center",
                    }
                );
                isEvidenceRequired = true;
            }
        });

        if (isEvidenceRequired) {
            setIsModalOpen(false);
            setIsSubmit(false);
            return;
        }

        
        const formData = new FormData();

        formData.append("assessmentId", assessmentId);
        formData.append("userId", 1);
        formData.append("levelId", activeLevelId);
        formData.append("organizationId", selectedEntity);
        formData.append("languageId", languageId);
        formData.append("version", 1);
        formData.append("isSubmitted", isSubmit);

        data.forEach((item, index) => {
            formData.append(`responses[${index}].questionId`, item.questionId);
            formData.append(`responses[${index}].userAnswer`, item.userAnswer);
            if (item.evidenceFile && typeof item.evidenceFile === "object") {
                formData.append(
                    `responses[${index}].evidenceFile`,
                    item.evidenceFile
                );
            }
            // else if (
            //     item.evidenceFile &&
            //     typeof item.evidenceFile === "string"
            // ) {
            //     formData.append(`responses[${index}].uri`, item.evidenceFile);
            // }
            formData.append(`responses[${index}].comments`, item.comments);
        });

        try {
            setLoading(true);
            const result = await submitAssessment(formData);
            setAssessmentResult(result?.data);
            setLevelScores(result?.data);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }

        setIsModalOpen(false);
        if (isSubmit) setActiveTab("Tab4");
        else {
            fetchPrevResponses();
            getData();
        }
    };

    const setLevelScores = (result) => {
        if (!result) return;

        const { levelId, isSubmitted, scorePercentage, aggregateScore } =
            result;

        setScores((prevState) => ({
            ...prevState,
            aggregateScore: aggregateScore || 0,
        }));

        const scoreKeyMap = {
            1: "basicScore",
            2: "developingScore",
            3: "matureScore",
        };

        const scoreKey = scoreKeyMap[levelId];
        if (!scoreKey) return;

        setScores((prevState) => ({
            ...prevState,
            [scoreKey]: isSubmitted ? scorePercentage : -1,
        }));
    };

    const getData = () => {
        const questionsPerLevel = assessmentQuestions || [];
        const responseIndex =
            assessmentResponses?.length > 0
                ? assessmentResponses.findIndex(
                      (item) => item.levelId === activeLevelId
                  )
                : -1;
        const newAssessment =
            assessmentResponses?.length > 0 &&
            assessmentResponses[0]?.aggregateScore === 100
                ? true
                : false;
        if (isReview) {
            const data = assessmentResult
                ? assessmentResult?.userResponses.map((item) => {
                      const question = questionsPerLevel.find(
                          (q) => q.id === item.questionId
                      );
                      return {
                          id: item.questionId,
                          questionText: question?.questionText,
                          userAnswer: item.userAnswer,
                          "option#1": question?.options[1],
                          "option#2": question?.options[2],
                          isCorrect: item.isCorrect,
                          comments: item.comments,
                          evidenceFile: item.uri,
                          hitachiGuidance: question?.hitachiGuidance,
                      };
                  })
                : [];
            return data;
        } else if (newAssessment) return questionsPerLevel;
        else if (isSave) {
            const data =
                assessmentResponses?.length > 0
                    ? assessmentResponses[responseIndex].userResponses.map(
                          (item) => {
                              const question = questionsPerLevel.find(
                                  (q) => q.id === item.questionId
                              );
                              return {
                                  id: item.questionId,
                                  questionText: question?.questionText,
                                  userAnswer: item.userAnswer,
                                  "option#1": question?.options[1],
                                  "option#2": question?.options[2],
                                  comments: item.comments,
                                  evidenceFile: item.uri,
                                  hitachiGuidance: question?.hitachiGuidance,
                              };
                          }
                      )
                    : [];
            return data;
        } else if (responseIndex !== -1) {
            const data = assessmentResponses[responseIndex].userResponses.map(
                (item) => {
                    const question = questionsPerLevel.find(
                        (q) => q.id === item.questionId
                    );
                    return {
                        id: item.questionId,
                        questionText: question?.questionText,
                        userAnswer: item.userAnswer,
                        "option#1": question?.options[1],
                        "option#2": question?.options[2],
                        comments: item.comments,
                        evidenceFile: item.uri,
                        hitachiGuidance: question?.hitachiGuidance,
                    };
                }
            );
            return data;
        } else return questionsPerLevel;
    };

    const getButtons = () => {
        if (isReview) {
            return (
                <div className="table-buttons-container">
                    <button
                        className="table-cancel-button"
                        onClick={() => {
                            setActiveTab("Tab4");
                            setIsReview(false);
                        }}
                    >
                        {t("close")}
                    </button>
                </div>
            );
        } else {
            return (
                <div className="table-buttons-container">
                    <button
                        className="cancelButton"
                        onClick={() => handleSubmit("cancel")}
                    >
                        {t("cancel")}
                    </button>
                    <button
                        className="secondaryButton"
                        onClick={() => handleSubmit("save")}
                    >
                        {t("save")}
                    </button>
                    <button
                        className="saveButton"
                        onClick={() => handleSubmit("submit")}
                    >
                        {t("submit")}
                    </button>
                </div>
            );
        }
    };

    if (loading)
        return (
            <div>
                <Loader />
            </div>
        );

    return (
        <div className="assessment-container">
            <h2 className="assessment-header">{templateName}</h2>

            <div className="arrow-box-container d-flex flex-grow-1 flex-wrap justify-content-center align-items-center ">
                {levelsData &&
                    levelsData.map((level) => (
                        <ArrowBox
                            key={level.levelId}
                            level={level}
                            isLast={
                                level.levelId ===
                                levelsData[levelsData.length - 1].levelId
                            }
                        />
                    ))}
            </div>
            <div className="ass-table">
                <AssessmentTable ref={assessmentTableRef} data={getData()} />
            </div>
            {getButtons()}

            {isModalOpen && (
                <div className="submit-modal">
                    <div className="submit-modal-content">
                        <h3>
                            {isCancel
                                ? t("cancel")
                                : isSubmit
                                  ? t("submit")
                                  : t("save")}{" "}
                            {t("confirmation")}
                        </h3>
                        <h4 className="svt-submit-modal">
                            <SubmitConfirmation />
                            {isCancel
                                ? t("areYouSureYouWantToCancelAssessment")
                                : isSubmit
                                  ? t("areYouSureYouWantToSubmitAssessment")
                                  : t("areYouSureYouWantToSaveAssessment")}{" "}
                        </h4>
                        {isCancel && (
                            <span style={{ marginLeft: "2.5rem" }}>
                                {t("unsavedChangesWillBeLost")}
                            </span>
                        )}
                        <div
                            className={`submit-modal-buttons ${
                                isCancel ? "cancel-mode-buttons" : ""
                            }`}
                        >
                            <button
                                className="cancelButton"
                                onClick={handleModalClose}
                            >
                                {t("cancel")}
                            </button>
                            <button
                                className="saveButton"
                                onClick={handleModalConfirm}
                            >
                                {isCancel
                                    ? t("confirm")
                                    : isSubmit
                                      ? t("submit")
                                      : t("save")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SelfVerificationAssessment;
